import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import client from "@/app/client";
import { useAuth } from "@/app/AuthContext";
import AffirmationCard from "@/components/wynn/AffirmationCard";
import { Wind, NotebookPen, ArrowUpRight } from "lucide-react";

const MOOD_LABEL = {
  open: "Open",
  tender: "Tender",
  restless: "Restless",
  heavy: "Heavy",
  quiet: "Quiet",
};

function greeting() {
  const h = new Date().getHours();
  if (h < 5) return "Still awake";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

const QUICK = [
  { key: "practices", label: "Take a breath", body: "A 5-minute reset", icon: Wind },
  { key: "journal", label: "Check in", body: "How are you arriving?", icon: NotebookPen },
];

export default function Home({ onNavigate }) {
  const { user } = useAuth();
  const [lastEntry, setLastEntry] = useState(null);

  useEffect(() => {
    client
      .get("/journal")
      .then((r) => setLastEntry(r.data.entries?.[0] || null))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="screen-home" className="space-y-10">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="text-[11px] uppercase tracking-[0.32em] text-[#F4F1EC]/55 font-body">
          {greeting()}
        </div>
        <h1 className="mt-3 font-display tracking-tight leading-[1.02] text-[clamp(2.2rem,6vw,3.4rem)] font-medium">
          {user?.name ? (
            <>Welcome back, <span className="italic font-light text-aurora">{user.name}</span>.</>
          ) : (
            <>Welcome <span className="italic font-light text-aurora">back</span>.</>
          )}
        </h1>
        <p className="mt-4 font-body text-[#F4F1EC]/65 text-lg leading-relaxed max-w-xl">
          A small space to return to yourself. Take what you need, then go back to the work.
        </p>
      </motion.div>

      <div
        data-testid="preview-banner"
        className="border border-[#FFB7C6]/25 bg-[#FFB7C6]/[0.04] px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
      >
        <p className="font-body text-sm text-[#F4F1EC]/75 leading-relaxed">
          This is a gentle <span className="text-[#FFB7C6]">preview</span> of WYNN. The full
          app — with instant-relief art therapy videos, AI coaching, meditations and more —
          is on its way.
        </p>
        <Link
          to="/#join"
          data-testid="preview-waitlist-link"
          className="shrink-0 font-body text-[11px] uppercase tracking-[0.2em] text-[#FFB7C6] hover:text-[#A8E6FF] transition-colors inline-flex items-center gap-1"
        >
          Join the waitlist <ArrowUpRight size={14} strokeWidth={1.6} />
        </Link>
      </div>

      <AffirmationCard />

      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#F4F1EC]/50 font-body mb-4">
          Where to begin
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {QUICK.map((q) => {
            const Icon = q.icon;
            return (
              <button
                key={q.key}
                data-testid={`home-quick-${q.key}`}
                onClick={() => onNavigate(q.key)}
                className="group text-left border border-[#F4F1EC]/12 bg-[#F4F1EC]/[0.03] p-6 hover:border-[#FFB7C6]/40 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <Icon size={20} strokeWidth={1.4} className="text-[#FFB7C6] group-hover:text-[#A8E6FF] transition-colors" />
                  <ArrowUpRight size={16} strokeWidth={1.6} className="text-[#F4F1EC]/40 group-hover:text-[#F4F1EC] transition-colors" />
                </div>
                <div className="mt-6 font-display text-[22px] leading-tight">{q.label}</div>
                <div className="mt-1 font-body text-sm text-[#F4F1EC]/55">{q.body}</div>
              </button>
            );
          })}
        </div>
      </div>

      {lastEntry && (
        <div className="border border-[#F4F1EC]/12 bg-[#F4F1EC]/[0.025] p-6">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#F4F1EC]/50 font-body">
            Last check-in
          </div>
          <p className="mt-3 font-display text-[20px] italic font-light text-[#F4F1EC]">
            You arrived <span className="text-aurora">{MOOD_LABEL[lastEntry.mood] || lastEntry.mood}</span>.
            {lastEntry.note ? ` "${lastEntry.note}"` : ""}
          </p>
        </div>
      )}
    </div>
  );
}
